/**
 * Where the functions run, and how to reach one.
 *
 * The region is here rather than in the functions because the app has to know
 * it too: a callable's address contains it, and the app builds that address
 * itself. Two copies of a deployment fact is how the emulators ended up serving
 * a project the app never looked at, so there is one copy and both sides import
 * it.
 *
 * Tel Aviv is the closest region to Addis Ababa on standard pricing. Doha is a
 * few hundred kilometres nearer and costs more for no latency a person could
 * feel; Johannesburg looks closer on a map but traffic out of Ethiopia usually
 * reaches it the long way round, through Europe.
 */

export const FUNCTIONS_REGION = "me-west1";

/**
 * The host half of a synthesised login address. It never receives mail; it is
 * allocated to the Firebase project, so there is no collision risk and no DNS
 * to own. Both the app and the functions build addresses, and an address that
 * differed between them would be an account nobody could sign into, so the
 * default lives here and each side may override it from its own environment.
 */
export const DEFAULT_LOGIN_DOMAIN = "liyu-sw.firebaseapp.com";

/** The Ethiopian clock the schedules run on. Fixed UTC+3, no daylight saving. */
export const TIME_ZONE = "Africa/Addis_Ababa";

/** The functions emulator's port, matching firebase.json. */
export const FUNCTIONS_EMULATOR_PORT = 5001;

/** The storage emulator's port, matching firebase.json. */
export const STORAGE_EMULATOR_PORT = 9199;

/**
 * The bucket a project's receipts live in.
 *
 * The native SDK reads it from google-services.json, but only when that file
 * carries it, and a build made before storage existed does not. The name is
 * derived from the project either way, so this is the fallback rather than a
 * reason for the screen to give up.
 */
export function storageBucketFor(projectId: string): string {
  return `${projectId}.firebasestorage.app`;
}

/**
 * Where a receipt is uploaded.
 *
 * The app reaches storage over its REST interface for the same reason it
 * reaches a callable that way: an upload is one POST, and a native module for
 * it would cost every developer a rebuild of the app shell. The emulator serves
 * the same shape on its own host.
 */
export function uploadUrl(options: {
  bucket: string;
  path: string;
  emulatorHost?: string;
}): string {
  const { bucket, path, emulatorHost } = options;
  const host = emulatorHost
    ? `http://${emulatorHost}:${String(STORAGE_EMULATOR_PORT)}`
    : "https://firebasestorage.googleapis.com";
  return `${host}/v0/b/${bucket}/o?uploadType=media&name=${encodeURIComponent(path)}`;
}

/** Where it is read back from, which is the same object with a download token. */
export function downloadUrl(options: {
  bucket: string;
  path: string;
  emulatorHost?: string;
}): string {
  const { bucket, path, emulatorHost } = options;
  const host = emulatorHost
    ? `http://${emulatorHost}:${String(STORAGE_EMULATOR_PORT)}`
    : "https://firebasestorage.googleapis.com";
  return `${host}/v0/b/${bucket}/o/${encodeURIComponent(path)}?alt=media`;
}

/**
 * Where a unit's receipts live.
 *
 * The organization, the unit and the occupancy are in the path so a storage
 * rule can decide who may look without reading a document: the resident's
 * claims must match all three, so a new occupant cannot open the last one's
 * receipts. The name is never reused because a receipt is written once.
 */
export function receiptPath(options: {
  orgId: string;
  unitId: string;
  termId: string;
  receiptId: string;
  extension: string;
}): string {
  const { orgId, unitId, termId, receiptId, extension } = options;
  return `organizations/${orgId}/receipts/${unitId}/${termId}/${receiptId}.${extension}`;
}

/**
 * A deployed callable lives at a hostname built from the region and project.
 * The emulator serves the same function under a path instead, which is why the
 * two shapes differ rather than one being the other with a host swapped.
 */
export function functionUrl(
  name: string,
  options: { projectId: string; emulatorHost?: string },
): string {
  const { projectId, emulatorHost } = options;
  if (!projectId) {
    throw new Error(`Cannot address ${name} without a project id`);
  }
  if (emulatorHost) {
    return `http://${emulatorHost}:${String(FUNCTIONS_EMULATOR_PORT)}/${projectId}/${FUNCTIONS_REGION}/${name}`;
  }
  return `https://${FUNCTIONS_REGION}-${projectId}.cloudfunctions.net/${name}`;
}
